/**
 * useVoiceChat Hook
 * Manages WebRTC connection to OpenAI Realtime API
 */

import { useState, useRef, useCallback } from 'react';
import VoiceApiService from '../services/VoiceApiService';
import { useVoice } from '../context/VoiceContext';
import memoryApi from '../api/memoryApi';

const useVoiceChat = (user, currentSessionId, navigate, refreshSessions) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const {
    showVoiceOverlay,
    hideVoiceOverlay, // eslint-disable-line no-unused-vars
    setMicAmplitude: updateContextAmplitude,
    setMicrophoneTrack
  } = useVoice();
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [requestSent, setRequestSent] = useState(false);
  const [transcript, setTranscriptState] = useState([]);
  const [micAmplitude, setMicAmplitude] = useState(0);
  const transcriptRef = useRef([]); // Keep transcript in ref for reliable access in disconnect

  // Wrapper to keep both state and ref in sync
  const setTranscript = useCallback((updater) => {
    setTranscriptState(prev => {
      const newValue = typeof updater === 'function' ? updater(prev) : updater;
      transcriptRef.current = newValue;
      return newValue;
    });
  }, []);

  // Voice transcript sequencing - AI text always follows user text
  const pendingAIResponseRef = useRef(null);
  const pendingUserMessageRef = useRef(null);

  // WebRTC refs
  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  const sessionIdRef = useRef(null);
  const chatSessionIdRef = useRef(currentSessionId); // Track chat session for saving
  const startTimeRef = useRef(null);

  /**
   * Initialize voice chat connection
   */
  const connect = useCallback(async () => {
    if (isConnecting || isConnected) return;

    // User check removed - voice chat works without user dependency

    try {
      setIsConnecting(true);
      setError(null);

      // Create session first if none exists (same flow as text chat)
      let activeSessionId = currentSessionId;
      if (!activeSessionId) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🎤 No session, creating one first...');
        }
        const sessionTitle = `Voice Chat - ${new Date().toLocaleDateString()}`;
        const newSession = await memoryApi.createSession(sessionTitle);
        activeSessionId = newSession.session_id;
        chatSessionIdRef.current = activeSessionId;

        // Refresh sidebar and navigate to new session
        if (refreshSessions) refreshSessions();
        if (navigate) {
          navigate(`/chatty/${activeSessionId}`, { replace: true });
        }
        if (process.env.NODE_ENV === 'development') {
          console.log('🎤 Created session:', activeSessionId);
        }
      } else {
        chatSessionIdRef.current = activeSessionId;
      }

      // Step 1: Get ephemeral token
      const tokenData = await VoiceApiService.getEphemeralToken();
      sessionIdRef.current = tokenData.session_id || 'unknown';
      startTimeRef.current = performance.now();

      // Step 2: Request microphone permission
      // List available devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(d => d.kind === 'audioinput');
      if (process.env.NODE_ENV === 'development') {
        console.log('🎤 Available audio inputs:', audioInputs.map(d => ({
          label: d.label || 'Unknown',
          deviceId: d.deviceId,
          groupId: d.groupId,
          kind: d.kind
        })));
      }

      // Also try to get what browser would pick without deviceId
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Testing browser default choice...');
      }
      try {
        const testStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const testTrack = testStream.getAudioTracks()[0];
        const testSettings = testTrack.getSettings();
        if (process.env.NODE_ENV === 'development') {
          console.log('📍 Browser default device info:', {
            label: testTrack.label,
            deviceId: testSettings.deviceId,
            sampleRate: testSettings.sampleRate,
            channelCount: testSettings.channelCount
          });
        }
        testStream.getTracks().forEach(track => track.stop()); // Clean up
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.log('❌ Could not test default:', err.message);
        }
      }

      // Check if we have any valid audio devices
      if (audioInputs.length === 0) {
        throw new Error('No audio input devices found');
      }

      // Detect if on Linux
      // eslint-disable-next-line no-unused-vars
      const isLinux = /Linux/.test(navigator.userAgent); // Reserved for Linux-specific audio handling

      // Filter out 100% fake audio devices (digital outputs incorrectly listed as inputs)
      const realDevices = audioInputs.filter(d => {
        const label = (d.label || '').toLowerCase();
        // These are NEVER real microphones - always digital audio outputs
        return !(
          label.includes('iec958') ||     // S/PDIF digital output
          label.includes('spdif') ||      // S/PDIF digital output
          label.includes('hdmi') ||       // HDMI audio output
          label.includes('displayport')   // DisplayPort audio output
        );
      });

      // Use first real device (not default)
      let selectedDevice = realDevices.find(d => d.deviceId !== 'default');

      let constraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 24000
        }
      };

      if (selectedDevice) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🎯 Using device:', selectedDevice.label);
        }
        constraints.audio.deviceId = { exact: selectedDevice.deviceId };
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('⚠️ Using browser default');
        }
      }

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      // Set microphone track in context for mute/unmute functionality
      const micTrack = stream.getAudioTracks()[0];
      if (micTrack) {
        setMicrophoneTrack(micTrack);
        // console.log('🎤 Microphone track set in context');
      }

      // Set up audio analysis for amplitude detection
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 256;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      microphone.connect(analyser);

      // Function to calculate amplitude
      const updateAmplitude = () => {
        analyser.getByteFrequencyData(dataArray);

        // Calculate average amplitude
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i];
        }
        const average = sum / bufferLength;

        // Normalize to 0.1-3 range for SiriWave (less flashy)
        const normalizedAmplitude = Math.max(0.1, (average / 255) * 3);
        setMicAmplitude(normalizedAmplitude);
        updateContextAmplitude(normalizedAmplitude);

        if (streamRef.current) {
          requestAnimationFrame(updateAmplitude);
        }
      };

      updateAmplitude();

      // Monitor microphone track events
      stream.getAudioTracks().forEach((track, index) => {

        track.onended = () => {
          if (process.env.NODE_ENV === 'development') {
            console.log(`❌ Audio track ${index} ENDED unexpectedly`);
          }
          setError('Microphone disconnected. Please restart voice chat.');
          disconnect();
        };

        track.onmute = () => {
          setError('Microphone muted. Please unmute to continue.');
        };

        track.onunmute = () => {
          setError(null);
        };
      });

      // Step 3: Create WebRTC peer connection
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      });
      pcRef.current = pc;

      // Monitor connection state
      pc.oniceconnectionstatechange = () => {
        if (pc.iceConnectionState === 'failed') {
          setError('WebRTC connection failed. Try reconnecting.');
        }
      };

      // Add microphone track
      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
      });


      // Handle incoming audio from AI
      pc.ontrack = (event) => {
        console.log('🔊 Received audio track from AI');
        if (audioRef.current) {
          audioRef.current.srcObject = event.streams[0];
        } else {
          const audioEl = document.createElement('audio');
          audioEl.autoplay = true;
          audioEl.playsInline = true;
          audioEl.srcObject = event.streams[0];

          // Debug audio events
          audioEl.addEventListener('loadstart', () => console.log('🔊 Audio: loadstart'));
          audioEl.addEventListener('canplay', () => console.log('🔊 Audio: canplay'));
          audioEl.addEventListener('play', () => console.log('🔊 Audio: play'));
          audioEl.addEventListener('pause', () => console.log('🔊 Audio: pause'));
          audioEl.addEventListener('ended', () => {
            console.log('🔊 AI audio playback finished - reverting to sparks');
            showVoiceOverlay('processing', true, () => {
              disconnect();
            });
          });

          audioRef.current = audioEl;
        }
      };

      // Monitor connection state
      pc.onconnectionstatechange = () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('📡 Connection state:', pc.connectionState);
        }
        if (pc.connectionState === 'connected') {
          console.log('🎤 Setting isConnected = true');
          setIsConnected(true);
          setIsConnecting(false);

          // IMMEDIATELY show ball overlay when WebRTC connects
          console.log('🎤 SHOWING BALL OVERLAY NOW');
          showVoiceOverlay('processing', true, () => {
            // End voice chat when X is clicked
            disconnect();
          });
        } else if (pc.connectionState === 'failed') {
          handleError(new Error('WebRTC connection failed'));
        } else if (pc.connectionState === 'disconnected') {
          if (process.env.NODE_ENV === 'development') {
            console.log('🔌 WebRTC disconnected - auto-ending session');
          }
          setIsConnected(false);
          // Auto-cleanup session when WebRTC disconnects (network drop, etc.)
          disconnect();
        }
      };

      // Step 4: Create data channel for events
      const dc = pc.createDataChannel('oai-events', { ordered: true });
      dcRef.current = dc;

      dc.onopen = () => {
        
        // Send initial session configuration with transcription enabled.
        // GA gpt-realtime session.update schema (verified against OpenAI
        // realtime docs 2026-06-24). The beta shape (top-level modalities/
        // voice/input_audio_transcription/turn_detection) is REJECTED by GA,
        // so instructions/VAD/transcription silently never apply. GA nests
        // audio config under session.audio.{input,output} and renames
        // modalities → output_modalities.
        sendEvent('session.update', {
          session: {
            type: 'realtime',
            output_modalities: ["audio"],
            instructions: `You are Chatty, a smart AI participant in a group conversation.
CRITICAL RULES for natural conversation:
- ONLY respond when someone is clearly addressing you (mentions "AI", "Chatty", "computer", or asks technical questions)
- If people are talking to each other about personal matters, stay SILENT
- If someone says "what do you think?" or "any ideas?" - only respond if the context suggests they want AI input
- Be like a polite human who knows when to speak and when to listen
- When uncertain if you're being addressed, stay SILENT
- Keep responses concise unless asked to elaborate
Speak in English.`,
            audio: {
              input: {
                transcription: {
                  model: 'whisper-1'  // Enable user speech transcription
                },
                turn_detection: {
                  type: 'server_vad',
                  threshold: 0.6,  // Less sensitive to background noise
                  prefix_padding_ms: 300,
                  silence_duration_ms: 1200  // Wait longer before responding (1.2 seconds)
                }
              },
              output: {
                voice: 'alloy'
              }
            },
            tools: []
          }
        });
      };

      dc.onmessage = (event) => {
        handleRealtimeEvent(JSON.parse(event.data));
      };

      dc.onerror = (error) => {
        console.error('❌ Data channel error:', error);
        handleError(new Error('Data channel failed'));
      };

      // Step 5: Create offer and connect
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpAnswer = await VoiceApiService.connectToRealtime(
        tokenData.ephemeral_token,
        offer.sdp,
        tokenData.model
      );

      await pc.setRemoteDescription({
        type: 'answer',
        sdp: sdpAnswer
      });

      // console.log('✅ Voice chat connected successfully');

    } catch (err) {
      console.error('❌ Voice chat connection failed:', err);
      handleError(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.token, isConnecting, isConnected, currentSessionId, navigate, refreshSessions]); // handleError excluded - defined as stable callback below

  /**
   * Disconnect voice chat
   */
  const disconnect = useCallback(async () => {
    // console.log('🔌 Disconnecting voice chat...');

    // Reset state immediately for instant UI feedback
    setIsConnected(false);
    setIsConnecting(false);
    setIsUserSpeaking(false);
    setIsAISpeaking(false);

    // Print any buffered AI response before disconnect
    if (pendingAIResponseRef.current) {
      if (process.env.NODE_ENV === 'development') {
        console.log('💬 Printing buffered AI response before disconnect');
      }
      setTranscript(prev => [...prev, pendingAIResponseRef.current]);
      pendingAIResponseRef.current = null;
    }

    // End voice session and adjust quota based on actual usage
    if (sessionIdRef.current) {
      try {
        // Calculate session duration for backend reference
        const sessionDurationMs = performance.now() - startTimeRef.current;
        const sessionDurationSeconds = sessionDurationMs / 1000;
        if (process.env.NODE_ENV === 'development') {
          console.log('🔚 Ending voice session:', sessionIdRef.current);
          console.log('⏱️ Frontend session duration:', sessionDurationSeconds.toFixed(3), 'seconds');
        }

        const endResponse = await VoiceApiService.endSession(sessionIdRef.current, sessionDurationSeconds);
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Voice session ended and quota adjusted');
        }

        // Trigger dashboard update with new usage percentage
        if (endResponse.usage_percentage !== undefined) {
          if (process.env.NODE_ENV === 'development') {
            console.log('📊 Usage percentage from Lambda:', endResponse.usage_percentage + '%');
          }
          // Dispatch custom event to update dashboard
          window.dispatchEvent(new CustomEvent('voiceUsageUpdate', {
            detail: { usagePercentage: endResponse.usage_percentage }
          }));
        }
      } catch (endError) {
        console.error('❌ Failed to end voice session:', endError);
        // Don't block disconnect on end session failure
      }
    }

    // Save voice transcript to existing session (created on connect)
    // Use transcriptRef for reliable access (state might be stale in callback)
    const currentTranscript = transcriptRef.current;
    if (currentTranscript.length > 0 && chatSessionIdRef.current) {
      try {
        if (process.env.NODE_ENV === 'development') {
          console.log('💾 Saving voice transcript to session:', chatSessionIdRef.current);
        }

        // Filter out any null/undefined messages before saving
        const filteredTranscript = currentTranscript.filter(msg => msg && msg.role && msg.text);

        // Deduplicate messages (same role, text, and timestamp)
        const cleanTranscript = filteredTranscript.filter((msg, index, arr) => {
          return !arr.slice(0, index).some(prevMsg =>
            prevMsg && msg && prevMsg.role === msg.role &&
            prevMsg.text === msg.text &&
            prevMsg.timestamp === msg.timestamp
          );
        });

        if (process.env.NODE_ENV === 'development') {
          console.log('🧹 Filtered transcript:', filteredTranscript.length, 'valid messages');
          console.log('🧹 Deduplicated transcript:', cleanTranscript.length, 'final messages');
        }

        if (cleanTranscript.length > 0) {
          // Convert voice transcript format to chat message format - use 'text' for frontend compatibility
          const chatMessages = cleanTranscript.map(msg => ({
            role: msg.role === 'ai' ? 'assistant' : msg.role,
            text: msg.text,
            timestamp: msg.timestamp,
            type: 'voice'
          }));

          // Fetch existing messages first, then append voice transcript
          const existingMessages = await memoryApi.getMessages(chatSessionIdRef.current);
          const allMessages = [...existingMessages, ...chatMessages];

          // Save combined messages to session
          await memoryApi.saveMessages(allMessages, chatSessionIdRef.current);

          if (process.env.NODE_ENV === 'development') {
            console.log('✅ Voice transcript saved, messages:', cleanTranscript.length);
          }
        }
      } catch (saveError) {
        console.error('❌ Failed to save voice transcript:', saveError);
        // Don't block disconnect on save failure
      }
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.log('🎤 Voice session ended, no transcript to save');
      }
    }

    // Clean up WebRTC resources with error handling
    try {
      // console.log('🔌 Cleaning up WebRTC resources...');
      
      if (streamRef.current) {
        // console.log('🎙️ Stopping microphone tracks...');
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        // Clear microphone track from context
        setMicrophoneTrack(null);
      }

      if (pcRef.current) {
        pcRef.current.close();
        pcRef.current = null;
      }

      if (dcRef.current) {
        dcRef.current.close();
        dcRef.current = null;
      }

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.srcObject = null;
        audioRef.current = null;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('✅ WebRTC cleanup completed');
      }
    } catch (cleanupError) {
      console.error('❌ Error during WebRTC cleanup:', cleanupError);
    }

    // Reset state immediately 
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Resetting voice chat state...');
    }
    setIsConnected(false);
    setIsConnecting(false);
    setIsUserSpeaking(false);
    setIsAISpeaking(false);
    setError(null);
    setTranscript([]);
    sessionIdRef.current = null;
    startTimeRef.current = null;
    
    
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Voice chat disconnected successfully');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript]); // setMicrophoneTrack excluded - stable context function

  /**
   * Send event to OpenAI via data channel
   */
  const sendEvent = useCallback((type, data = {}) => {
    if (dcRef.current?.readyState === 'open') {
      const event = { type, ...data };
      dcRef.current.send(JSON.stringify(event));
      if (process.env.NODE_ENV === 'development') {
        console.log('📤 Sent event:', type);
      }
    }
  }, []);

  /**
   * Handle errors
   */
  const handleError = useCallback((err) => {
    setError(err.message);
    setErrorDetails({
      code: err.code || 'unknown',
      message: err.message,
      resetTime: err.resetTime || null,
      resetDate: err.resetDate || null,
      quotaLimit: err.quotaLimit || null,
      quotaUsed: err.quotaUsed || null
    });
    setIsConnecting(false);
    disconnect();
  }, [disconnect]);

  const clearError = useCallback(() => {
    setError(null);
    setErrorDetails(null);
    setRequestSent(false);
  }, []);

  const requestQuotaIncrease = useCallback(async () => {
    try {
      await VoiceApiService.requestVoiceAccess();
      setRequestSent(true);
    } catch (e) {
      console.error('❌ Quota increase request failed:', e);
      setRequestSent(false);
    }
  }, []);

  /**
   * Handle realtime events from OpenAI - LOG EVERYTHING to find STT events
   */
  const handleRealtimeEvent = useCallback((event) => {
    // No event logging - keep console clean
    
    switch (event.type) {
      case 'session.created':
        break;

      case 'conversation.item.created':
        if (process.env.NODE_ENV === 'development') {
          console.log('📝 Conversation item created:', {
            id: event.item?.id,
            role: event.item?.role,
            status: event.item?.status,
            hasContent: !!event.item?.content?.[0],
            contentType: event.item?.content?.[0]?.type
          });
        }
        // Handle both completed and in_progress messages
        if (event.item?.type === 'message' && event.item?.content?.[0]) {
          const content = event.item.content[0];
          const messageText = content.transcript || content.text;
          
          if (messageText) {
            const message = {
              role: event.item.role === 'assistant' ? 'ai' : event.item.role,
              text: messageText,
              timestamp: new Date().toISOString(),
              type: 'voice'
            };
            if (process.env.NODE_ENV === 'development') {
              console.log(`${event.item.role === 'user' ? '👤' : '🤖'} Message:`, messageText.substring(0, 50));
            }
            setTranscript(prev => [...prev, message]);
          } else {
            if (process.env.NODE_ENV === 'development') {
              console.log('⚠️ No transcript/text found in content:', content);
            }
          }
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.log('⚠️ Invalid message structure:', event.item);
          }
        }
        break;

      case 'input_audio_buffer.speech_started':
        setIsUserSpeaking(true);
        setIsAISpeaking(false);
        // Switch to processing state when user starts speaking
        showVoiceOverlay('processing', true, () => {
          disconnect();
        });
        // When user starts speaking, print any buffered AI response first
        if (pendingAIResponseRef.current) {
          if (process.env.NODE_ENV === 'development') {
            console.log('🗣️ User started speaking - printing buffered AI first');
          }
          const aiMessage = pendingAIResponseRef.current;
          pendingAIResponseRef.current = null;
          setTranscript(prev => [...prev, aiMessage]);
        }
        break;

      case 'input_audio_buffer.speech_stopped':
        setIsUserSpeaking(false);
        break;

      case 'input_audio_buffer.committed':
        break;

      case 'input_audio_transcription.completed':
        break;

      case 'conversation.item.input_audio_transcription.completed':
        const finalUserText = event.transcript;
        const userTimestamp = new Date().toISOString();
        if (finalUserText) {
          const userMessage = {
            role: 'user',
            text: finalUserText,
            timestamp: userTimestamp,
            type: 'voice'
          };
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`\n📊 SEQUENCE [${userTimestamp}]`);
            console.log(`  1️⃣ USER VOICE DONE: "${finalUserText}"`);
            console.log(`  2️⃣ USER TEXT PROCESSED: Immediately displaying to UI`);
          }
          
          // ALWAYS display user text first (with duplicate check)
          setTranscript(prev => {
            // Check if this exact message already exists
            const messageExists = prev.some(msg =>
              msg && msg.role === userMessage.role &&
              msg.text === userMessage.text &&
              msg.timestamp === userMessage.timestamp
            );

            if (messageExists) {
              if (process.env.NODE_ENV === 'development') {
                console.log(`  3️⃣ USER TEXT SKIPPED: Already exists in transcript`);
              }
              return prev;
            }

            const newTranscript = [...prev, userMessage];
            if (process.env.NODE_ENV === 'development') {
              console.log(`  3️⃣ USER TEXT PRINTED: ${newTranscript.length} messages, latest role: user`);
            }
            return newTranscript;
          });
          
          // Then check for pending AI response from previous exchange
          if (pendingAIResponseRef.current) {
            if (process.env.NODE_ENV === 'development') {
              console.log(`  4️⃣ FOUND PENDING AI: "${pendingAIResponseRef.current.text.substring(0, 50)}..."`);
              console.log(`  5️⃣ AI TEXT PROCESSED: Printing buffered AI after user text`);
            }
            const aiMessage = pendingAIResponseRef.current;
            pendingAIResponseRef.current = null;
            setTranscript(prev => {
              const newTranscript = [...prev, aiMessage];
              if (process.env.NODE_ENV === 'development') {
                console.log(`  6️⃣ AI TEXT PRINTED: ${newTranscript.length} messages, latest role: ai`);
              }
              return newTranscript;
            });
          } else {
            if (process.env.NODE_ENV === 'development') {
              console.log(`  4️⃣ NO PENDING AI: Storing user text, waiting for AI response`);
              console.log(`  5️⃣ USER TEXT PROCESSED: Stored for later AI pairing`);
            }
            // Store user message for AI transcript to find later
            pendingUserMessageRef.current = userMessage;
          }
        } else {
          if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ User transcription completed but no text received');
        }
        }
        break;

      case 'response.created':
        setIsAISpeaking(true);
        // Switch to speaking state when AI starts responding
        showVoiceOverlay('speaking', true, () => {
          disconnect();
        });
        break;

      // GA renamed these (response.audio.* → response.output_audio.*). Handle
      // BOTH so the UI stays correct on GA gpt-realtime or a beta fallback
      // model — explicit dual-vocabulary branch, not a silent fallback.
      case 'response.audio.delta':
      case 'response.output_audio.delta':
        // AI is actively speaking - show SiriWave
        showVoiceOverlay('speaking', true, () => {
          disconnect();
        });
        break;

      case 'response.audio.done':
      case 'response.output_audio.done':
        setIsAISpeaking(false);
        // Don't revert immediately - wait for actual audio playback to finish
        // Audio 'ended' event will handle the transition back to sparks
        break;

      case 'response.audio_transcript.done':
      case 'response.output_audio_transcript.done':
        const finalAIText = event.transcript;
        const aiTimestamp = new Date().toISOString();
        if (finalAIText) {
          const aiMessage = {
            role: 'ai',
            text: finalAIText,
            timestamp: aiTimestamp,
            type: 'voice'
          };
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`\n📊 SEQUENCE [${aiTimestamp}]`);
            console.log(`  1️⃣ AI VOICE DONE: "${finalAIText.substring(0, 50)}..."`);
          }
          
          // Check if user message is waiting for this AI response
          if (pendingUserMessageRef.current) {
            if (process.env.NODE_ENV === 'development') {
              console.log(`  2️⃣ FOUND PENDING USER: "${pendingUserMessageRef.current.text.substring(0, 30)}..."`);
              console.log(`  3️⃣ AI TEXT PROCESSED: Printing with stored user text`);
            }
            const userMessage = pendingUserMessageRef.current;
            pendingUserMessageRef.current = null;
            setTranscript(prev => {
              const newTranscript = [...prev, userMessage, aiMessage];
              if (process.env.NODE_ENV === 'development') {
                console.log(`  4️⃣ BOTH TEXTS PRINTED: ${newTranscript.length} messages, user + AI together`);
              }
              return newTranscript;
            });
          } else {
            if (process.env.NODE_ENV === 'development') {
              console.log(`  2️⃣ BUFFERING AI: Wait for user text to print first`);
              console.log(`  3️⃣ AI TEXT PROCESSED: Stored for later user pairing`);
            }
            // Buffer AI - will display immediately AFTER user text prints
            pendingAIResponseRef.current = aiMessage;
          }
        } else {
          if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ AI transcript done but no text received');
        }
        }
        break;

      case 'conversation.item.audio_transcription.completed':
        // Audio transcription completed
        break;

      case 'response.done':
        const doneTimestamp = new Date().toISOString();
        if (process.env.NODE_ENV === 'development') {
          console.log(`\n📊 SEQUENCE [${doneTimestamp}]`);
          console.log(`  ✅ AI RESPONSE COMPLETE`);
        }
        // Don't print orphaned text - let it wait for user text
        break;

      case 'error':
        console.error('❌ Realtime error:', event.error);
        handleError(new Error(event.error.message || 'OpenAI Realtime error'));
        break;

      default:
        // All messages handled by conversation.item.created - no fallback needed
        break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleError]); // sendEvent excluded - would cause re-render loop

  /**
   * Toggle voice chat (connect/disconnect)
   */
  const toggleVoiceChat = useCallback(() => {
    if (isConnected || isConnecting) {
      disconnect();
    } else {
      connect();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, isConnecting, connect, disconnect]); // showVoiceOverlay excluded - stable context function


  // Return early state if no user (after all hooks are called)
  if (!user) {
    return {
      isConnected: false,
      isConnecting: false,
      isUserSpeaking: false,
      isAISpeaking: false,
      error: 'Please log in to use voice chat',
      errorDetails: null,
      requestSent: false,
      transcript: [],
      connect: () => {},
      disconnect: () => {},
      toggleVoiceChat: () => {},
      clearError: () => {},
      requestQuotaIncrease: async () => {},
      isRecording: false,
      connectionStatus: 'disconnected'
    };
  }

  return {
    // State
    isConnected,
    isConnecting,
    isUserSpeaking,
    isAISpeaking,
    error,
    errorDetails,
    requestSent,
    transcript,
    micAmplitude,

    // Actions
    connect,
    disconnect,
    toggleVoiceChat,
    clearError,
    requestQuotaIncrease,

    // Computed state
    isRecording: isConnected && (isUserSpeaking || isAISpeaking),
    connectionStatus: isConnecting ? 'connecting' :
                     isConnected ? 'connected' :
                     error ? 'error' : 'disconnected'
  };
};

export default useVoiceChat;